import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CreditCard, Star, Shield, Gem, TrendingUp, Award, ArrowRight } from 'lucide-react';

export const TrumpGoldCard = () => {
  const features = [
    {
      icon: Gem,
      title: 'Premium Materials',
      description: 'Crafted from premium materials with gold accents for a distinctive luxury feel.',
    },
    {
      icon: Shield,
      title: 'Enhanced Security',
      description: 'Advanced fraud protection and secure transaction monitoring 24/7.',
    },
    {
      icon: TrendingUp,
      title: 'Exclusive Rewards',
      description: 'Earn premium rewards points on every purchase with accelerated earning rates.',
    },
    {
      icon: Award,
      title: 'VIP Access',
      description: 'Priority customer service and exclusive access to luxury experiences.',
    },
    {
      icon: Star,
      title: 'Global Acceptance',
      description: 'Accepted worldwide with no foreign transaction fees for international travel.',
    },
    {
      icon: CreditCard,
      title: 'Flexible Spending',
      description: 'High credit limits and flexible payment options tailored to your needs.',
    },
  ];

  const benefits = [
    'Complimentary airport lounge access worldwide',
    'Travel insurance and purchase protection',
    'Concierge services available 24/7',
    'Cashback on dining and entertainment',
    'Annual statement credits for luxury purchases',
    'Priority booking for exclusive events',
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1758908176211-5bd0932f956a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwcGF5bWVudCUyMGNhcmR8ZW58MHx8fHwxNzczNzM1Njg0fDA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>

        {/* Floating gold elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#FFD700] rounded-full filter blur-3xl opacity-20 animate-pulse" />
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl opacity-20 animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-[#FFD700] rounded-full flex items-center justify-center shadow-2xl shadow-[#FFD700]/50">
              <CreditCard className="w-10 h-10 text-[#0B2A4A]" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Trump Gold Card
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
            Premium financial card offering exclusive services and financial tools
          </p>
          <Link to="/card-application">
            <Button className="bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-bold px-10 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD700]/50 transform hover:scale-105 transition-all duration-300 group">
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1757185161725-92fafe8a50fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwcGF5bWVudCUyMGNhcmR8ZW58MHx8fHwxNzczNzM1Njg0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Premium Gold Card"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FFD700] rounded-full filter blur-3xl opacity-20" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Elevate Your Financial Experience
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  The Trump Gold Card represents the pinnacle of premium financial services, designed for individuals who demand excellence in every aspect of their financial life.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  This exclusive card combines cutting-edge financial technology with personalized service, offering a suite of benefits that enhance your lifestyle while providing sophisticated financial tools.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  From luxury travel perks to premium rewards programs, the Trump Gold Card delivers unparalleled value and prestige to cardholders worldwide.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Grid */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Premium Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Exclusive benefits designed for discerning cardholders
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#FFD700] to-[#FFD700]/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                        <feature.icon className="w-8 h-8 text-[#0B2A4A]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Benefits Section */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-8">
                  Unmatched Benefits
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start group">
                      <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                      </div>
                      <p className="text-lg text-gray-600 leading-relaxed group-hover:text-[#0B2A4A] transition-colors">
                        {benefit}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1758551652244-45a6889b9451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwcGF5bWVudCUyMGNhcmR8ZW58MHx8fHwxNzczNzM1Njg0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Premium Card Benefits"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#0B2A4A] rounded-full filter blur-3xl opacity-10" />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Card Tiers */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                Choose Your Tier
              </h2>
              <p className="text-xl text-gray-600">
                Select the card that matches your lifestyle
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-white shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-gray-200">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-8 h-8 text-gray-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">Gold</h3>
                    <p className="text-gray-600 mt-2">Essential Benefits</p>
                  </div>
                  <div className="space-y-3 mb-8">
                    <p className="text-gray-600">• Standard rewards rate</p>
                    <p className="text-gray-600">• Basic travel insurance</p>
                    <p className="text-gray-600">• Customer support</p>
                    <p className="text-gray-600">• Purchase protection</p>
                  </div>
                  <Link to="/card-application">
                    <Button className="w-full bg-gray-600 hover:bg-gray-700 text-white">
                      Apply Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#FFD700] to-[#FFD700]/80 shadow-2xl hover:shadow-[#FFD700]/50 transition-all duration-500 transform hover:-translate-y-4 border-4 border-[#FFD700] relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#0B2A4A] text-white px-4 py-1 rounded-full text-sm font-bold">
                    RECOMMENDED
                  </span>
                </div>
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#0B2A4A] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Star className="w-8 h-8 text-[#FFD700]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B2A4A]">Platinum</h3>
                    <p className="text-[#0B2A4A] mt-2">Premium Benefits</p>
                  </div>
                  <div className="space-y-3 mb-8">
                    <p className="text-[#0B2A4A]">• Enhanced rewards rate</p>
                    <p className="text-[#0B2A4A]">• Comprehensive insurance</p>
                    <p className="text-[#0B2A4A]">• Priority support</p>
                    <p className="text-[#0B2A4A]">• Lounge access</p>
                    <p className="text-[#0B2A4A]">• Concierge services</p>
                  </div>
                  <Link to="/card-application">
                    <Button className="w-full bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white">
                      Apply Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-[#0B2A4A]">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Gem className="w-8 h-8 text-[#0B2A4A]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Elite</h3>
                    <p className="text-gray-300 mt-2">Exclusive Benefits</p>
                  </div>
                  <div className="space-y-3 mb-8">
                    <p className="text-gray-300">• Maximum rewards rate</p>
                    <p className="text-gray-300">• Elite travel coverage</p>
                    <p className="text-gray-300">• Dedicated account manager</p>
                    <p className="text-gray-300">• Global lounge access</p>
                    <p className="text-gray-300">• Luxury experiences</p>
                  </div>
                  <Link to="/card-application">
                    <Button className="w-full bg-[#FFD700] hover:bg-[#FFD700]/90 text-[#0B2A4A]">
                      Apply Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection>
        <section className="py-24 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl opacity-10" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience Premium?
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Join the exclusive community of Trump Gold Card holders today.
            </p>
            <Link to="/card-application">
              <Button className="bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-bold px-10 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD700]/50 transform hover:scale-110 transition-all duration-300 group">
                Apply for Your Card
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default TrumpGoldCard;
